/**
 * Frontend Notification Service
 * Handles API calls to the backend notification endpoints
 */

import authService from '../services/authService.js';

class NotificationService {
  constructor() {
    // Request deduplication - prevent duplicate simultaneous calls
    this.pendingRequests = new Map();
    
    // Temporary cache to handle React StrictMode double renders
    this.responseCache = new Map();
    this.cacheTimeout = 3000; // 3 seconds cache for notifications
  }
  
  /**
   * Clean expired cache entries on-demand (lazy cleanup)
   */
  _cleanExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.responseCache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.responseCache.delete(key);
      }
    }
  }
  
  /**
   * Make cached API request with deduplication
   */
  async _cachedRequest(endpoint, options = {}) {
    // Lazy cleanup
    if (this.responseCache.size > 0) {
      this._cleanExpiredCache();
    }
    
    // Skip caching for POST/PUT requests (they modify data)
    if (!options.method || options.method === 'GET') {
      const cacheKey = `GET:${endpoint}`;
      const cached = this.responseCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.response;
      }
    }
    
    // Create unique request key
    const requestKey = `${options.method || 'GET'}:${endpoint}`;
    
    // Return existing promise if in progress
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }
    
    // Make the actual request
    const requestPromise = authService.request(endpoint, options);
    
    // Store pending request
    this.pendingRequests.set(requestKey, requestPromise);
    
    // Cache response and cleanup
    requestPromise
      .then(response => {
        if (!options.method || options.method === 'GET') {
          const cacheKey = `GET:${endpoint}`;
          this.responseCache.set(cacheKey, {
            response,
            timestamp: Date.now()
          });
        }
        return response;
      })
      .finally(() => {
        this.pendingRequests.delete(requestKey);
      });
    
    return requestPromise;
  }
  
  /**
   * Get notifications for the current user
   */
  async getNotifications(options = {}) {
    try {
      const {
        limit = 50,
        offset = 0,
        includeRead = true,
        includeDeleted = false,
        type = null,
        category = null
      } = options;

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        includeRead: includeRead.toString(),
        includeDeleted: includeDeleted.toString(),
      });

      if (type) params.append('type', type);
      if (category) params.append('category', category);

      const response = await this._cachedRequest(`/notifications?${params.toString()}`);
      
      return {
        success: true,
        notifications: response.notifications || [],
        count: response.count || 0
      };
    } catch (error) { 
      return {
        success: false,
        error: error.message,
        notifications: [],
        count: 0
      };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    try {
      const response = await this._cachedRequest('/notifications/unread-count');
      
      return {
        success: true,
        count: response.count || 0
      };
    } catch (error) { 
      return {
        success: false,
        count: 0,
        error: error.message
      };
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId) {
    try {
      const response = await authService.request(`/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      
      return {
        success: true,
        notification: response.notification
      };
    } catch (error) { 
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const response = await authService.request('/notifications/mark-all-read', {
        method: 'POST'
      });
      
      return {
        success: true,
        message: response.message
      };
    } catch (error) { 
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId) {
    try {
      const response = await authService.request(`/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      
      return {
        success: true,
        message: response.message
      };
    } catch (error) { 
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Clear all notifications
   */
  async clearAllNotifications() {
    try {
      const response = await authService.request('/notifications', {
        method: 'DELETE'
      });
      
      return {
        success: true,
        message: response.message
      };
    } catch (error) { 
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Format notification for display
   */
  formatNotification(notification) {
    return {
      ...notification,
      timestamp: new Date(notification.created_at),
      isRead: notification.is_read,
      formattedTime: this.formatTimestamp(notification.created_at),
      icon: this.getNotificationIcon(notification.type),
      color: this.getNotificationColor(notification.priority)
    };
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  }

  /**
   * Get icon for notification type
   */
  getNotificationIcon(type) {
    const icons = {
      upgrade: '✨',
      cancellation: '💔',
      payment_success: '✅',
      payment_failed: '⚠️',
      reactivation: '🎉',
      downgrade: '📉',
      ritual: '🔮',
      archetype: '🌟',
      energy_limit: '⚡',
      maintenance: '🔧',
      welcome: '👋',
      general: '📢'
    };

    return icons[type] || icons.general;
  }

  /**
   * Get color class for notification priority
   */
  getNotificationColor(priority) {
    const colors = {
      low: 'text-gray-400',
      normal: 'text-indigo-400',
      high: 'text-blue-400',
      urgent: 'text-red-400'
    };

    return colors[priority] || colors.normal;
  }
}

// Create and export singleton instance
export const notificationService = new NotificationService();
export default notificationService;